import useVisibleProjects from "../hooks/useVisibleProjects";
// src/pages/ProjectsPage.js
import React, { useMemo, useState, useEffect } from "react";
import LoadingIndicator from "../components/LoadingIndicator";
import { FaChevronDown, FaPlus, FaTimes, FaTrash } from "react-icons/fa";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";

// Stilkonstanter
const boxStyle = {
  position: "relative",
  display: "inline-block",
  flex: "0 1 220px",
  minWidth: 150,
  maxWidth: 260,
};

const inputStyle = {
  width: "100%",
  height: 50,
  padding: "8px 34px 8px 12px",
  border: "1px solid #d1d5db",
  borderRadius: 3,
  background: "#ffffff",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle = {
  position: "absolute",
  top: -10,
  left: 12,
  fontSize: "0.7rem",
  background: "#ffffff",
  padding: "0 4px",
  color: "#6b7280",
  userSelect: "none",
};

const toolbarButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 42,
  height: 42,
  borderRadius: 8,
  color: "white",
  fontSize: 18,
  padding: 0,
  boxSizing: "border-box",
  cursor: "pointer",
};

// Hjälpfunktion för att få aktuell månad på svenska
function getCurrentMonthInSwedish() {
  const months = [
    "Januari", "Februari", "Mars", "April", "Maj", "Juni",
    "Juli", "Augusti", "September", "Oktober", "November", "December"
  ];
  const currentMonthIndex = new Date().getMonth();
  return months[currentMonthIndex];
}

// Huvudkomponent för projekthantering
export default function ProjectsPage() {
  const { currentUser, profile } = useAuth();
  const { activeCompany, loading: companyLoading } = useCompany();

  // State för filtrering och sökning
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState(String(new Date().getFullYear()));
  const [monthFilter, setMonthFilter] = useState(getCurrentMonthInSwedish());
  const [schemaFilter, setSchemaFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  
  // State för popup-fönster
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // State för data
  const [allProjects, setProjects] = useState([]);
  const projects = useVisibleProjects(allProjects);
  const [customers, setCustomers] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingError, setSavingError] = useState("");

  // State för nytt projekt
  const [newProject, setNewProject] = useState({
    name: "",
    area: "",
    customerId: "",
    customerName: "",
    description: "",
    price: "",
  });

  // Hämta companyAccountUid från activeCompany
  const companyAccountUid = activeCompany?.companyAccountUid;

  // Skapa array med tillgängliga år (nuvarande år och några år framåt/bakåt)
  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    // Visa 5 år bakåt och 2 år framåt från nuvarande år
    for (let year = currentYear - 5; year <= currentYear + 2; year++) {
      years.push(String(year));
    }
    return years;
  }, []);

  // Hämta projekt från Firestore - använder companyAccountUid
  useEffect(() => {
    if (!companyAccountUid) {
      setProjects([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    console.log("Hämtar projekt för företag:", activeCompany?.companyName);

    const projectsRef = collection(db, "users", companyAccountUid, "projects");

    const unsubscribe = onSnapshot(
      projectsRef,
      (snapshot) => {
        const projectList = [];

        snapshot.forEach((projectDoc) => {
          const data = projectDoc.data();

          projectList.push({
            _id: projectDoc.id,
            name: data.name || "Projekt saknas",
            area: data.area || "",
            customerId: data.customerId || "",
            customerName: data.customerName || "",
            description: data.description || "",
            price: data.price ?? "",
            createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt || null,
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt || null,
          });
        });

        projectList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setProjects(projectList);
        setLoading(false);
      },
      (error) => {
        console.error("Fel vid hämtning av projekt:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid, activeCompany?.companyName]);

  // Hämta kunder för dropdown - använder companyAccountUid
  useEffect(() => {
    if (!companyAccountUid) return;

    const customersRef = collection(db, "users", companyAccountUid, "customers");

    const unsubscribe = onSnapshot(
      customersRef,
      (snapshot) => {
        const customerList = [];

        snapshot.forEach((customerDoc) => {
          const data = customerDoc.data();
          customerList.push({
            _id: customerDoc.id,
            name: data.name || "Namn saknas",
          });
        });

        customerList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCustomers(customerList);
      },
      (error) => {
        console.error("Fel vid hämtning av kunder:", error);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid]);

  // Hämta scheman för dropdown - använder companyAccountUid
  useEffect(() => {
    if (!companyAccountUid) return;

    const schemasRef = collection(db, "users", companyAccountUid, "Scheduling");

    const unsubscribe = onSnapshot(
      schemasRef,
      (snapshot) => {
        const schemaList = [];
        snapshot.forEach((schemaDoc) => {
          const data = schemaDoc.data();
          schemaList.push({
            _id: schemaDoc.id,
            name: data.name || "Namnlöst schema",
          });
        });

        schemaList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setSchemas(schemaList);
      },
      (error) => {
        console.error("Fel vid hämtning av scheman:", error);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid]);

  // Hämta pass för att kunna filtrera projekt baserat på schema - använder companyAccountUid
  useEffect(() => {
    if (!companyAccountUid) return;

    const shiftsRef = collection(db, "users", companyAccountUid, "shifts");

    const unsubscribe = onSnapshot(
      shiftsRef,
      (snapshot) => {
        const shiftList = [];

        snapshot.forEach((shiftDoc) => {
          shiftList.push({
            _id: shiftDoc.id,
            ...shiftDoc.data(),
          });
        });

        setShifts(shiftList);
      },
      (error) => {
        console.error("Fel vid hämtning av pass:", error);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid]);

  // Hjälpfunktion för att normalisera projektnamn
  const normalizeProjectName = (value) => {
    return (value || "").trim() || "Projekt saknas";
  };

  // Skapa en uppsättning av projekt som har pass i valt schema
  const projectsWithShiftsInSelectedSchema = useMemo(() => {
    if (!schemaFilter) return null; // Om inget schema är valt, returnera null (visa alla)

    const projectSet = new Set();

    shifts.forEach((shift) => {
      const shiftSchemaId = shift.schemaId || shift.schema || "";
      if (shiftSchemaId === schemaFilter) {
        const projectName = normalizeProjectName(shift.project);
        projectSet.add(projectName);
      }
    });

    return projectSet;
  }, [shifts, schemaFilter]);

  // Filtrera projekt baserat på sökning, kund, schema, år och månad
  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      // Först kolla om projektet har pass i valt schema (om schema är valt)
      if (schemaFilter && projectsWithShiftsInSelectedSchema) {
        const normalizedProjectName = normalizeProjectName(project.name);
        if (!projectsWithShiftsInSelectedSchema.has(normalizedProjectName)) {
          return false; // Projektet har inga pass i valt schema - filtrera bort
        }
      }

      // Sen applicera övriga filter
      const searchMatch =
        !searchTerm ||
        (project.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.area || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.customerName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (project.description || "").toLowerCase().includes(searchTerm.toLowerCase());

      const customerMatch = !customerFilter || project.customerId === customerFilter;

      return searchMatch && customerMatch;
    });
  }, [projects, searchTerm, customerFilter, schemaFilter, projectsWithShiftsInSelectedSchema]);

  // Hantera ändringar i nytt projekt-formuläret
  const handleNewProjectChange = (e) => {
    const { name, value } = e.target;
    setNewProject((prev) => ({
      ...prev,
      [name]: value,
    }));
    setSavingError("");
  };

  // Hantera ändringar i redigeringsformuläret
  const handleEditProjectChange = (e) => {
    const { name, value } = e.target;
    setSelectedProject((prev) => ({
      ...prev,
      [name]: value,
    }));
    setSavingError("");
  };

  // Hantera val av kund för nytt projekt
  const handleCustomerSelect = (customerId) => {
    const selected = customers.find(c => c._id === customerId);
    setNewProject(prev => ({
      ...prev,
      customerId: customerId,
      customerName: selected ? selected.name : "",
    }));
  };

  // Hantera val av kund för redigering
  const handleEditCustomerSelect = (customerId) => {
    const selected = customers.find(c => c._id === customerId);
    setSelectedProject(prev => ({
      ...prev,
      customerId: customerId,
      customerName: selected ? selected.name : "",
    }));
  };

  // Spara nytt projekt
  const handleSaveProject = async () => {
    try {
      setSavingError("");

      if (!companyAccountUid) {
        setSavingError("Inget företagskonto hittades.");
        return;
      }

      if (!newProject.name.trim()) {
        setSavingError("Projektnamn måste fyllas i.");
        return;
      }

      if (!newProject.customerId) {
        setSavingError("Välj en kund.");
        return;
      }

      if (newProject.price !== "" && (!Number.isFinite(Number(newProject.price)) || Number(newProject.price) < 0)) {
        setSavingError("Priset måste vara ett giltigt belopp som är 0 eller högre.");
        return;
      }

      const newProjectRef = doc(collection(db, "users", companyAccountUid, "projects"));

      await setDoc(newProjectRef, {
        name: newProject.name.trim(),
        area: newProject.area.trim(),
        customerId: newProject.customerId,
        customerName: newProject.customerName,
        description: newProject.description.trim(),
        price: newProject.price === "" ? null : Number(newProject.price),
        active: true,
        createdAt: new Date(),
      });

      setShowAddPopup(false);
      setNewProject({
        name: "",
        area: "",
        customerId: "",
        customerName: "",
        description: "",
        price: "",
      });
    } catch (error) {
      console.error("Fel vid sparande av projekt:", error);
      setSavingError(`Det gick inte att spara projektet: ${error.message}`);
    }
  };

  // Uppdatera befintligt projekt
  const handleUpdateProject = async () => {
    try {
      setSavingError("");

      if (!companyAccountUid || !selectedProject?._id) {
        setSavingError("Projektinformation saknas.");
        return;
      }

      if (!selectedProject.name.trim()) {
        setSavingError("Projektnamn måste fyllas i.");
        return;
      }

      if (!selectedProject.customerId) {
        setSavingError("Välj en kund.");
        return;
      }

      if (selectedProject.price !== "" && (!Number.isFinite(Number(selectedProject.price)) || Number(selectedProject.price) < 0)) {
        setSavingError("Priset måste vara ett giltigt belopp som är 0 eller högre.");
        return;
      }

      const projectRef = doc(db, "users", companyAccountUid, "projects", selectedProject._id);

      await updateDoc(projectRef, {
        name: selectedProject.name.trim(),
        area: selectedProject.area?.trim() || "",
        customerId: selectedProject.customerId,
        customerName: selectedProject.customerName,
        description: selectedProject.description?.trim() || "",
        price: selectedProject.price === "" ? null : Number(selectedProject.price),
        updatedAt: new Date(),
      });

      setShowEditPopup(false);
      setSelectedProject(null);
    } catch (error) {
      console.error("Fel vid uppdatering av projekt:", error);
      setSavingError(`Det gick inte att uppdatera projektet: ${error.message}`);
    }
  };

  // Ta bort projekt
  const handleDeleteProject = async (projectId) => {
    try {
      if (!companyAccountUid || !projectId) return;

      const projectRef = doc(db, "users", companyAccountUid, "projects", projectId);
      await deleteDoc(projectRef);
      
      setDeleteConfirm(null);
      if (showEditPopup) {
        setShowEditPopup(false);
        setSelectedProject(null);
      }
    } catch (error) {
      console.error("Fel vid borttagning av projekt:", error);
      alert("Det gick inte att ta bort projektet. Försök igen.");
    }
  };

  // Öppna redigeringspopup för ett projekt
  const handleEditClick = (project) => {
    setSelectedProject(project);
    setShowEditPopup(true);
    setDeleteConfirm(null);
  };

  // Månadsalternativ för filter
  const monthOptions = [
    "Januari",
    "Februari",
    "Mars",
    "April",
    "Maj",
    "Juni",
    "Juli",
    "Augusti",
    "September",
    "Oktober",
    "November",
    "December",
  ];

  // Visa laddningsindikator medan företag laddas
  if (companyLoading || loading) {
    return <LoadingIndicator message="Laddar projekt..." fullPage />;
  }

  // Visa meddelande om inget företag är valt
  if (!activeCompany) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
        <p>Inget företag valt. Vänligen logga in igen.</p>
        <button
          onClick={() => window.location.href = "/login"}
          style={{
            padding: "10px 20px",
            background: "#6323ef",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            marginTop: 16,
          }}
        >
          Gå till inloggning
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "0 0 24px" }}>
      {/* Visa aktivt företag */}
      <div style={{ marginBottom: 16, padding: "0 0 8px" }}>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Visar projekt för: <strong>{activeCompany?.companyName}</strong>
        </p>
      </div>

      {/* Verktygsrad med filter och knappar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 0 20px",
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          flexWrap: "nowrap",
        }}
      >
        {/* Sökfält */}
        <div style={{ ...boxStyle, minWidth: 320, maxWidth: 420, flex: "1 1 360px" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Sök på projektnamn, område, kund eller beskrivning..."
            style={inputStyle}
          />
        </div>

        {/* Schema-filter */}
        <div style={boxStyle}>
          <select
            value={schemaFilter}
            onChange={(e) => setSchemaFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            <option value="">Alla scheman</option>
            {schemas.map((schema) => (
              <option key={schema._id} value={schema._id}>
                {schema.name}
              </option>
            ))}
          </select>

          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Schema</span>
        </div>

        {/* Kund-filter */}
        <div style={boxStyle}>
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            <option value="">Alla kunder</option>
            {customers.map((customer) => (
              <option key={customer._id} value={customer._id}>
                {customer.name}
              </option>
            ))}
          </select>

          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Kund</span>
        </div>

        {/* År-filter */}
        <div style={boxStyle}>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>År</span>
        </div>

        {/* Månad-filter */}
        <div style={boxStyle}>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {monthOptions.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>

          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Månad</span>
        </div>

        {/* Lägg till projekt-knapp */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setShowAddPopup(true)}
            style={{
              ...toolbarButtonStyle,
              background: "#3b82f6",
              border: "1px solid #2563eb",
            }}
          >
            <FaPlus />
          </button>
        </div>
      </div>

      {/* Rubrik med antal projekt */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "16px 18px",
          marginBottom: 14,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            color: "#111827",
          }}
        >
          Projekt {!loading && `(${filteredProjects.length})`}
        </h1>
        {schemaFilter && (
          <div style={{ marginTop: 8, fontSize: 14, color: "#6b7280" }}>
            Visar endast projekt med pass i valt schema
          </div>
        )}
      </div>

      {/* Projekt-tabell */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {/* Tabellhuvud */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 0.8fr 1.2fr 1.8fr 0.8fr auto",
            gap: 12,
            padding: "14px 16px",
            borderBottom: "1px solid #e5e7eb",
            background: "#f9fafb",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>PROJEKTNAMN</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>OMRÅDE/STAD</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>KUND</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>BESKRIVNING</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>TIMPRIS</div>
          <div></div>
        </div>

        {/* Tabellrader */}
        {loading ? (
          <LoadingIndicator message="Laddar projekt..." />
        ) : filteredProjects.length > 0 ? (
          filteredProjects.map((project) => {
            return (
              <div
                key={project._id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 0.8fr 1.2fr 1.8fr 0.8fr auto",
                  gap: 12,
                  alignItems: "center",
                  padding: "14px 16px",
                  borderBottom: "1px solid #f1f5f9",
                  background: "#ffffff",
                  borderLeft: "4px solid transparent",
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {project.name}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {project.area || "-"}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {project.customerName || "-"}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#6b7280",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {project.description || "-"}
                </div>

                <div style={{ fontSize: 14, color: "#374151" }}>
                  {project.price === "" || project.price == null
                    ? "-"
                    : `${Number(project.price).toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr/tim`}
                </div>

                <div style={{ textAlign: "right" }}>
                  <button
                    onClick={() => handleEditClick(project)}
                    style={{
                      background: "none",
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      width: 38,
                      height: 38,
                      cursor: "pointer",
                      fontSize: 18,
                      color: "#374151",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ⋮
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "#6b7280",
              fontWeight: 500,
            }}
          >
            {schemaFilter ? (
              <>Inga projekt med pass i valt schema hittades.</>
            ) : (
              <>Inga projekt hittades.</>
            )}
          </div>
        )}
      </div>

      {/* Lägg till projekt-popup */}
      {showAddPopup && (
        <div
          onClick={() => {
            setShowAddPopup(false);
            setSavingError("");
            setNewProject({
              name: "",
              area: "",
              customerId: "",
              customerName: "",
              description: "",
              price: "",
            });
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: 520,
              maxWidth: "90vw",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 22,
                  color: "#111827",
                }}
              >
                Lägg till projekt
              </h3>

              <button
                onClick={() => {
                  setShowAddPopup(false);
                  setSavingError("");
                  setNewProject({
                    name: "",
                    area: "",
                    customerId: "",
                    customerName: "",
                    description: "",
                    price: "",
                  });
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 20,
                  color: "#6b7280",
                }}
              >
                <FaTimes />
              </button>
            </div>

            {savingError && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  color: "#991b1b",
                  padding: "10px",
                  borderRadius: "6px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              >
                {savingError}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 14,
              }}
            >
              <input
                name="name"
                value={newProject.name}
                onChange={handleNewProjectChange}
                placeholder="Projektnamn *"
                style={inputStyle}
              />
              
              <input
                name="area"
                value={newProject.area}
                onChange={handleNewProjectChange}
                placeholder="Område/Stad"
                style={inputStyle}
              />

              <select
                value={newProject.customerId}
                onChange={(e) => handleCustomerSelect(e.target.value)}
                style={{ ...inputStyle, paddingRight: 12 }}
              >
                <option value="">Välj kund *</option>
                {customers.map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {customer.name}
                  </option>
                ))}
              </select>

              <input
                type="number"
                name="price"
                value={newProject.price}
                onChange={handleNewProjectChange}
                placeholder="Timpris (kr/timme, valfritt)"
                aria-label="Timpris (kr/timme, valfritt)"
                min="0"
                step="0.01"
                style={inputStyle}
              />

              <textarea
                name="description"
                value={newProject.description}
                onChange={handleNewProjectChange}
                placeholder="Beskrivning"
                style={{
                  ...inputStyle,
                  height: 100,
                  padding: "12px",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              * Obligatoriska fält
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 18,
              }}
            >
              <button
                onClick={handleSaveProject}
                style={{
                  height: 42,
                  padding: "0 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#2563eb",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Spara
              </button>

              <button
                onClick={() => {
                  setShowAddPopup(false);
                  setSavingError("");
                  setNewProject({
                    name: "",
                    area: "",
                    customerId: "",
                    customerName: "",
                    description: "",
                    price: "",
                  });
                }}
                style={{
                  height: 42,
                  padding: "0 16px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  color: "#111827",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redigera projekt-popup */}
      {showEditPopup && selectedProject && (
        <div
          onClick={() => {
            setShowEditPopup(false);
            setSelectedProject(null);
            setDeleteConfirm(null);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: 520,
              maxWidth: "90vw",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 22,
                  color: "#111827",
                }}
              >
                Redigera projekt
              </h3>

              <button
                onClick={() => {
                  setShowEditPopup(false);
                  setSelectedProject(null);
                  setDeleteConfirm(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 20,
                  color: "#6b7280",
                }}
              >
                <FaTimes />
              </button>
            </div>

            {savingError && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  color: "#991b1b",
                  padding: "10px",
                  borderRadius: "6px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              >
                {savingError}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 14,
              }}
            >
              <input
                name="name"
                value={selectedProject.name}
                onChange={handleEditProjectChange}
                placeholder="Projektnamn *"
                style={inputStyle}
              />
              
              <input
                name="area"
                value={selectedProject.area}
                onChange={handleEditProjectChange}
                placeholder="Område/Stad"
                style={inputStyle}
              />

              <select
                value={selectedProject.customerId}
                onChange={(e) => handleEditCustomerSelect(e.target.value)}
                style={{ ...inputStyle, paddingRight: 12 }}
              >
                <option value="">Välj kund *</option>
                {customers.map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {customer.name}
                  </option>
                ))}
              </select>

              <input
                type="number"
                name="price"
                value={selectedProject.price}
                onChange={handleEditProjectChange}
                placeholder="Timpris (kr/timme, valfritt)"
                aria-label="Timpris (kr/timme, valfritt)"
                min="0"
                step="0.01"
                style={inputStyle}
              />

              <textarea
                name="description"
                value={selectedProject.description}
                onChange={handleEditProjectChange}
                placeholder="Beskrivning"
                style={{
                  ...inputStyle,
                  height: 100,
                  padding: "12px",
                  resize: "vertical",
                  fontFamily: "inherit",
                }}
              />
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              * Obligatoriska fält
            </div>

            {deleteConfirm === selectedProject._id ? (
              <div
                style={{
                  marginTop: 18,
                  padding: 16,
                  background: "#fee2e2",
                  borderRadius: 8,
                  border: "1px solid #fecaca",
                }}
              >
                <p style={{ margin: "0 0 12px 0", color: "#991b1b", fontWeight: 500 }}>
                  Är du säker på att du vill ta bort detta projekt?
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => handleDeleteProject(selectedProject._id)}
                    style={{
                      height: 38,
                      padding: "0 16px",
                      borderRadius: 8,
                      border: "none",
                      background: "#dc2626",
                      color: "#fff",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Ja, ta bort
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    style={{
                      height: 38,
                      padding: "0 16px",
                      borderRadius: 8,
                      border: "1px solid #9ca3af",
                      background: "#fff",
                      color: "#374151",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginTop: 18,
                }}
              >
                <button
                  onClick={handleUpdateProject}
                  style={{
                    height: 42,
                    padding: "0 16px",
                    borderRadius: 10,
                    border: "none",
                    background: "#2563eb",
                    color: "#fff",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Spara ändringar
                </button>

                <button
                  onClick={() => {
                    setShowEditPopup(false);
                    setSelectedProject(null);
                    setDeleteConfirm(null);
                  }}
                  style={{
                    height: 42,
                    padding: "0 16px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    color: "#111827",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Avbryt
                </button>

                <button
                  onClick={() => setDeleteConfirm(selectedProject._id)}
                  style={{
                    height: 42,
                    padding: "0 16px",
                    borderRadius: 10,
                    border: "1px solid #fecaca",
                    background: "#fee2e2",
                    color: "#dc2626",
                    fontWeight: 600,
                    cursor: "pointer",
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FaTrash /> Ta bort
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
